import { Cpu, Disc3, Truck, ClipboardCheck, Droplets, Zap, Settings, ThermometerSun, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import diagnosticsImg from "@/assets/diagnostics.jpg";
import brakesImg from "@/assets/brakes.jpg";
import roadsideImg from "@/assets/roadside.jpg";
import inspectionImg from "@/assets/inspection.jpg";

const mainServices = [
  {
    icon: Cpu,
    title: "Mobile Engine Diagnostics",
    description: "Computer-based fault scanning & live data analysis using professional OBD-II tools. Our engineering background ensures accurate diagnosis the first time.",
    image: diagnosticsImg,
    features: ["OBD-II live data", "Fault code analysis", "Engine performance test"],
  },
  {
    icon: Disc3,
    title: "Brake & Suspension",
    description: "Complete brake pad replacement, disc machining, shock absorber service, and suspension alignment — all at your location.",
    image: brakesImg,
    features: ["Pad & disc replacement", "Shock absorber service", "Wheel alignment"],
  },
  {
    icon: Truck,
    title: "Roadside Recovery",
    description: "Stranded on Nanyuki Road? We provide rapid roadside assistance and recovery across Thika Town and surrounding areas within 30 minutes.",
    image: roadsideImg,
    features: ["30-min response time", "Thika-wide coverage", "Emergency repairs"],
  },
  {
    icon: ClipboardCheck,
    title: "Pre-Purchase Inspections",
    description: "Buying a used car? Our thorough 150-point engineering inspection gives you confidence before you commit your money.",
    image: inspectionImg,
    features: ["150-point checklist", "Written report", "Market value assessment"],
  },
];

const additionalServices = [
  { icon: Droplets, title: "Oil & Filter Change", desc: "Premium engine oils and OEM filters delivered and changed on-site." },
  { icon: Zap, title: "Battery & Electrical", desc: "Battery testing, replacement, alternator checks, and wiring repairs." },
  { icon: Settings, title: "General Repairs", desc: "Clutch replacement, timing belt, water pump, and general mechanical work." },
  { icon: ThermometerSun, title: "Cooling System", desc: "Radiator flush, thermostat replacement, and coolant system diagnostics." },
];

const Services = () => (
  <>
    <PageHeader title="Our Services" subtitle="Engineering-grade auto repair, delivered to your doorstep" breadcrumb="Services" />

    <section className="section-padding bg-background">
      <div className="container mx-auto">
        <div className="space-y-16 sm:space-y-20">
          {mainServices.map((service, index) => (
            <div
              key={service.title}
              className={`grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-12 items-center ${index % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""}`}
            >
              <div className="relative group">
                <div className="aspect-[16/10] overflow-hidden rounded-xl shadow-elevated">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                </div>
                {/* Decorative accent */}
                <div className={`absolute -bottom-3 ${index % 2 === 1 ? '-left-3' : '-right-3'} w-full h-full border-2 border-primary/15 rounded-xl -z-10`} />
              </div>
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 shadow-sm">
                    <service.icon className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
                  </div>
                  <h3 className="font-heading text-xl sm:text-2xl md:text-3xl uppercase text-foreground">{service.title}</h3>
                </div>
                <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mb-5">{service.description}</p>
                
                {/* Feature pills */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {service.features.map((f) => (
                    <span key={f} className="px-3 py-1 rounded-full bg-primary/8 text-primary text-[10px] sm:text-xs font-heading uppercase tracking-wider border border-primary/15">
                      {f}
                    </span>
                  ))}
                </div>

                <Button variant="hero" size="lg" asChild>
                  <a href="https://wa.me/254700000000?text=Hi%2C%20I%20need%20help%20with%20" target="_blank" rel="noopener noreferrer">
                    Book This Service <ArrowRight className="h-4 w-4 ml-1" />
                  </a>
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="section-padding bg-card relative overflow-hidden">
      <div className="absolute top-0 right-0 w-80 h-80 bg-primary/3 rounded-full blur-[120px]" />
      <div className="container mx-auto relative z-10">
        <div className="text-center mb-10 sm:mb-14">
          <p className="font-heading uppercase tracking-[0.3em] text-primary text-xs sm:text-sm mb-3">And More</p>
          <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl uppercase text-foreground mb-4">Additional Services</h2>
          <div className="premium-divider mx-auto max-w-[80px]" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {additionalServices.map((s) => (
            <div key={s.title} className="bg-background border border-border/50 rounded-xl p-5 sm:p-6 hover:border-primary/40 hover:shadow-elevated transition-all duration-500 group hover:-translate-y-1 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative z-10">
                <div className="p-3 rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 w-fit mb-4 group-hover:shadow-premium transition-all duration-500">
                  <s.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-heading text-base sm:text-lg uppercase text-foreground mb-2">{s.title}</h3>
                <p className="text-muted-foreground text-xs sm:text-sm leading-relaxed">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-10 sm:mt-14">
          <Button variant="heroOutline" size="lg" asChild>
            <Link to="/pricing">
              View Pricing <ArrowRight className="h-4 w-4 ml-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  </>
);

export default Services;
